import { useEffect, useRef, useState } from 'react'
import { Camera, ExternalLink, FileText, ImageIcon, Upload, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetServiceStampPhotos, useOpenServiceStamp, useRecordServiceStamp, useSignServiceStampUpload,
} from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { DOCUMENT_ACCEPT, checkDocument, uploadSigned, type Signed } from '../../utils/signedUpload'
import { km, kmEntry, shortDate } from '../../utils/serviceBoard'

/**
 * The service book stamp: upload it, and look at it.
 *
 * UPLOADING A STAMP RECORDS THE SERVICE IT SHOWS. The date and the odometer are
 * read off the stamp by the person holding it and filed with the photographs,
 * so the board measures the next service from the book rather than from an
 * inferred figure. If the same service is already on file (same date, same
 * odometer, recorded by a person), the photographs are filed against it and
 * nothing is recorded twice. Recording a service also closes the car's open
 * booking, which switches the due alerts back on for the next cycle. All of
 * that happens in vehicle_service_record_stamp_v1; this only moves the files.
 */

function today(): string {
  // The date in Johannesburg, which is the operator's day.
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}

const link =
  'mt-1 rounded text-xs font-medium text-[hsl(var(--brand))] underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'

export type StampTarget = {
  vehicle_id: string
  plate: string | null
  // Prefills: the service on file when the stamp is for it, or the booking
  // being closed.
  serviced_on?: string | null
  odometer_km?: number | null
  provider_name?: string | null
  // One line under the title saying what this stamp closes, if anything.
  context?: string | null
}

export function UploadStampDialog({
  target,
  onSaved,
  label = 'Upload stamp',
}: {
  target: StampTarget
  onSaved: () => void
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [date, setDate] = useState('')
  const [odo, setOdo] = useState('')
  const [provider, setProvider] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const sign = useSignServiceStampUpload<Signed>()
  const save = useRecordServiceStamp<{ attached_to_existing?: boolean; booking_closed?: string | null; next_due_km?: number }>()
  const parsed = kmEntry(odo)

  useEffect(() => {
    if (open) {
      setFiles([])
      setDate(target.serviced_on?.slice(0, 10) ?? '')
      setOdo(target.odometer_km ? String(target.odometer_km) : '')
      setProvider(target.provider_name ?? '')
      setNotes('')
    }
  }, [open, target.serviced_on, target.odometer_km, target.provider_name])

  function add(list: FileList | null) {
    if (!list) return
    const next = [...files]
    for (const f of Array.from(list)) {
      const bad = checkDocument(f)
      if (bad) {
        toast.error(`${f.name}: ${bad}`)
        continue
      }
      if (next.length >= 6) {
        toast.error('Six photographs is the most one service takes.')
        break
      }
      next.push(f)
    }
    setFiles(next)
    if (input.current) input.current.value = ''
  }

  async function submit() {
    if (parsed === null || files.length === 0 || !date) return
    setBusy(true)
    try {
      // Upload every file first; the service is recorded only once all of them
      // are in storage, so a dropped connection never leaves half a record.
      const uploaded: { path: string; filename: string }[] = []
      for (const f of files) {
        const signed = await sign.trigger({ vehicle_id: target.vehicle_id, filename: f.name }, { skipCache: true }).result
        uploaded.push({ path: await uploadSigned(signed, f), filename: f.name })
      }
      const r = await save.trigger({
        vehicle_id: target.vehicle_id,
        files: uploaded,
        serviced_on: date,
        odometer_km: parsed,
        provider_name: provider.trim() || null,
        notes: notes.trim() || null,
      }).result
      const parts = [
        r?.attached_to_existing ? 'Filed against the service already on record.' : `Service recorded at ${km(parsed)}.`,
        r?.booking_closed ? `Booking ${r.booking_closed} is closed and alerts resume for the next service.` : null,
        r?.next_due_km ? `Next service due at ${km(r.next_due_km)}.` : null,
      ].filter(Boolean)
      toast.success(`Stamp filed on ${target.plate ?? 'the vehicle'}`, { description: parts.join(' ') })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not file that stamp', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  const id = target.vehicle_id
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
      <DialogTrigger asChild>
        <button type="button" className={link} aria-label={`Upload the service book stamp for ${target.plate ?? 'this vehicle'}`}>
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Service book stamp, {target.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            Photograph the stamped page and write the date and the odometer exactly as the stamp shows
            them. The next service is measured from this.
            {target.context ? ` ${target.context}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`stamp-files-${id}`}>Photographs</Label>
            <input
              ref={input}
              id={`stamp-files-${id}`}
              type="file"
              accept={DOCUMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => add(e.target.files)}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()} disabled={busy}>
              <Camera className="mr-2 h-4 w-4" /> Choose or take photos
            </Button>
            {files.length > 0 ? (
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-xs">
                    {f.type === 'application/pdf' ? <FileText className="h-3.5 w-3.5 shrink-0" /> : <ImageIcon className="h-3.5 w-3.5 shrink-0" />}
                    <span className="min-w-0 flex-1 truncate">{f.name}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      disabled={busy}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">A photo or a PDF, up to six, 10 MB each.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`stamp-date-${id}`}>Date on the stamp</Label>
              <Input id={`stamp-date-${id}`} type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`stamp-km-${id}`}>Odometer on the stamp</Label>
              <Input
                id={`stamp-km-${id}`}
                inputMode="numeric"
                value={odo}
                onChange={(e) => setOdo(e.target.value)}
                placeholder="e.g. 152 011"
              />
            </div>
          </div>
          {odo.trim() !== '' && parsed === null && (
            <p className="text-xs text-red-700 dark:text-red-400">
              Write the odometer as a whole number of kilometres, spaces or commas allowed.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`stamp-prov-${id}`}>
              Workshop <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Input id={`stamp-prov-${id}`} value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Whose stamp it is" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`stamp-notes-${id}`}>
              Note <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Textarea id={`stamp-notes-${id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" disabled={busy || files.length === 0 || !date || parsed === null} onClick={submit}>
            {busy ? <Spinner className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
            File the stamp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type Photo = {
  id: string
  path: string
  filename: string | null
  mime_type: string | null
  uploaded_by: string | null
  uploaded_at: string | null
  serviced_on: string | null
  odometer_km: number | null
}

export function ViewStampsDialog({ vehicleId, plate }: { vehicleId: string; plate: string | null }) {
  const [open, setOpen] = useState(false)
  const photos = useGetServiceStampPhotos<Photo[]>()
  const openStamp = useOpenServiceStamp<{ url: string }>()
  const [urls, setUrls] = useState<Record<string, string>>({})
  const load = photos.trigger

  useEffect(() => {
    if (open) load({ vehicle_id: vehicleId }, { skipCache: true })
  }, [open, vehicleId, load])

  // A link per photograph, valid for an hour, so the images show in the dialog.
  const data = photos.data
  const list = open ? data ?? [] : []
  const ask = openStamp.trigger
  useEffect(() => {
    if (!open || !data) return
    let live = true
    for (const p of data) {
      ask({ path: p.path })
        .result.then((r) => {
          if (live && r?.url) setUrls((u) => ({ ...u, [p.path]: r.url }))
        })
        .catch(() => {
          // The tile keeps its spinner; the photograph is still listed with its date.
        })
    }
    return () => {
      live = false
    }
  }, [open, data, ask])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={`${link} ml-2`} aria-label={`View the service book stamps for ${plate ?? 'this vehicle'}`}>
          View stamp
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Service book stamps, {plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>Every stamp filed for this car, newest service first.</DialogDescription>
        </DialogHeader>
        {photos.loading && list.length === 0 ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading
          </div>
        ) : photos.error ? (
          <p className="text-sm text-red-700 dark:text-red-400">The stamps could not be read: {photos.error}</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">No stamp is filed for this car yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {list.map((p) => {
              const url = urls[p.path]
              const isPdf = (p.mime_type ?? p.path).toLowerCase().includes('pdf')
              return (
                <figure key={p.id + p.path} className="rounded-md border border-border p-2">
                  {url && !isPdf ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`Service book stamp, ${shortDate(p.serviced_on)}`} className="max-h-72 w-full rounded object-contain" />
                    </a>
                  ) : (
                    <div className="flex h-32 items-center justify-center rounded bg-muted text-muted-foreground">
                      {url ? <FileText className="h-8 w-8" /> : <Spinner className="h-4 w-4" />}
                    </div>
                  )}
                  <figcaption className="mt-2 text-xs">
                    <div className="font-medium text-foreground">
                      {shortDate(p.serviced_on)}, {km(p.odometer_km)}
                    </div>
                    <div className="text-muted-foreground">
                      Filed by {p.uploaded_by ?? 'not recorded'}, {shortDate(p.uploaded_at)}
                    </div>
                    {url && (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[hsl(var(--brand))] hover:underline">
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </figcaption>
                </figure>
              )
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
